import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { Injectable, Type } from "@angular/core";
import { Subject, BehaviorSubject, Subscription } from "rxjs";
import {
  BmarkFixingType,
  BmarkFixingData,
  ProfilePromiseActionService,
  ActiveUserProfilePromiseService,
  ProfileSettingKeys,
  ActiveUser,
  BmarkFixingOperation,
  SystemConfigPromiseService,
  Roles,
  BenchmarkDataPrimaryLookupService,
  HedgeOrderDataPrimaryLookupService,
} from "shared/cubiq-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import { BmarkSummaryAlertStatus } from "../models/bmark-summary-alert-status.model";

import { GoldenLayoutService } from "../../golden-layout/golden-layout-service";

// Filtering dependencies
import { filter } from "rxjs";
import { BfxBlotterFilterService } from "app/bfx-blotter-filter/bfx-blotter-filter.service";
import { BmarkOrderData } from "./../../../shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import { BmarkClientOrdersFilterService } from "./../blotters/client-orders/services/bmark-client-orders-filter.service";
import {
  FilterType,
  BfxBlotterFilterId,
  ExtendedOrderTypeIdentifier,
  BfxBlotterFilter,
} from "./../../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BmarkSummaryUtilService, FilterAppliedStatusModel } from "./bmark-summary-util.service";
import { OELoggerService } from "../../order-entry/services/basic/oe-logger.service";
import { FilterConfiguration } from "../../bfx-blotter-filter/FilterConfiguration";

const PROFILE_KEY = ProfileSettingKeys.userBmarkSummaryPreferences;

export enum FixingTabType {
  SPOT_RISK = "SPOT_RISK",
  FWD_RISK = "FWD_RISK",
  CLIENT_ORDERS = "CLIENT_ORDERS",
  HEDGE_ORDERS = "HEDGE_ORDERS",
  FIXING_AUDIT = "FIXING_AUDIT,",
}
export type FixingActiveTab = {
  tabIndex: number;
  activeTabType: FixingTabType;
};

type FixingType = {
  id: string;
  state: CubiqTS.FixingState;
};

@Injectable()
export class BmarkFixingsManagerService {
  /**********************************************************************/
  /* DATA */
  public data: {
    interfaceOnline: boolean;
    displayedFixingsType: BmarkFixingType;
    selectedFixingId: string;
    selectedFixingType: BmarkFixingType;
    pendingSelectAfterRemove: {
      timeoutId: NodeJS.Timeout;
      fixingId: string;
    };
    requestedFixingId: string;
    requestedFixingType: BmarkFixingType;
    activeFixingsList: Array<BmarkFixingData>;
    recentFixingsList: Array<BmarkFixingData>;
    activeFixingsIndexes: WeakMap<BmarkFixingData, number>;
    recentFixingsIndexes: WeakMap<BmarkFixingData, number>;
    lastFixingStates: WeakMap<BmarkFixingData, number>;
    activeTabIndex: number;
    alertStatus: BmarkSummaryAlertStatus;
    hcoIdsList: Set<string>;
    scoIdsList: Set<string>;
    isFilterApplied: boolean;
    filterConfiguration: FilterConfiguration;
  } = {
    interfaceOnline: false,
    displayedFixingsType: BmarkFixingType.ACTIVE,
    selectedFixingId: null,
    selectedFixingType: null,
    pendingSelectAfterRemove: {
      timeoutId: null,
      fixingId: null,
    },
    requestedFixingId: null,
    requestedFixingType: null,
    activeFixingsList: [],
    recentFixingsList: [],
    activeFixingsIndexes: new WeakMap(),
    recentFixingsIndexes: new WeakMap(),
    lastFixingStates: new WeakMap(),
    activeTabIndex: null,
    alertStatus: null,
    hcoIdsList: new Set<string>(),
    scoIdsList: new Set<string>(),
    isFilterApplied: false,
    filterConfiguration: [],
  };

  public permissions: {
    canViewSpotRisk: boolean;
    canViewFwdRisk: boolean;
    canViewClientOrders: boolean;
    canViewHedgeOrders: boolean;
    bmRiskRouteEnabled: boolean;
    canUseCircuitBreaker: boolean;
    canViewBenchmarkAudit: boolean;
  } = {
    canViewSpotRisk: false,
    canViewFwdRisk: false,
    canViewClientOrders: false,
    canViewHedgeOrders: false,
    bmRiskRouteEnabled: false,
    canUseCircuitBreaker: false,
    canViewBenchmarkAudit: false,
  };

  /** Object storing active tab number for view - will be -1 if view is disabled */
  private tabIndices: {
    spotRisk: number;
    fwdRisk: number;
    clientOrders: number;
    hedgeOrders: number;
    audit: number;
  } = {
    spotRisk: -1,
    fwdRisk: -1,
    clientOrders: -1,
    hedgeOrders: -1,
    audit: -1,
  };

  // flag whether to update the alert status or not
  private reRunUpdateEvent = false;

  /* RxJS - Outgoing */
  private activeTabSource = new BehaviorSubject<FixingActiveTab>({
    tabIndex: 0,
    activeTabType: FixingTabType.SPOT_RISK,
  });
  private displayFixingEventSource = new BehaviorSubject<BmarkFixingData | null>(
    this.findDisplayedFixing(this.data.selectedFixingId),
  );
  private retrievingOrdersEventSource = new Subject<void>();
  private activeFixingListEventSource = new BehaviorSubject<Array<BmarkFixingData> | null>(
    this.data.activeFixingsList,
  );
  private recentFixingListEventSource = new BehaviorSubject<Array<BmarkFixingData> | null>(
    this.data.recentFixingsList,
  );
  private summaryAlertStatusSource = new BehaviorSubject<BmarkSummaryAlertStatus>(
    this.data.alertStatus,
  );
  public activeTab$ = this.activeTabSource.asObservable();
  public displayFixing$ = this.displayFixingEventSource.asObservable();
  public retrievingOrdersForFixing$ = this.retrievingOrdersEventSource.asObservable();
  public activeFixingsList$ = this.activeFixingListEventSource.asObservable();
  public recentFixingsList$ = this.recentFixingListEventSource.asObservable();
  public summaryAlertStatus$ = this.summaryAlertStatusSource.asObservable();
  /* RxJS - Incoming */
  private fixingsUpdateSubscription: Subscription;
  private hedgeFixingsUpdateSubscription: Subscription;

  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    protected oeLogger: OELoggerService,
    //private translate: TranslateService,
    private _bmarkDataLookup: BenchmarkDataPrimaryLookupService,
    private _hedgeDataLookup: HedgeOrderDataPrimaryLookupService,
    private _profileService: ProfilePromiseActionService,
    private _userProfileService: BfxUserProfileService,
    private _activeUserService: ActiveUserProfilePromiseService,
    private _sysConfigLookup: SystemConfigPromiseService,
    private _layoutService: GoldenLayoutService,
    private _bmarkUtilService: BmarkSummaryUtilService,
    private _blotterFilterService: BfxBlotterFilterService,
    private _bmarkFilteringService: BmarkClientOrdersFilterService, // private _bmarkClientOrdersDataService: BmarkClientOrdersDataService
  ) {
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initializePermissions().then(() => {
      this.initialize();
    });
  }

  protected initialize(): void {
    let lookupPromises: Promise<boolean>[] = [];
    let fixingPromises: Promise<void>[] = [];

    // Ensure we know which tabs have which functionality - order of checking must be equal to order in view
    let tabIndex = 0;
    this.tabIndices.spotRisk = this.permissions.canViewSpotRisk ? tabIndex++ : -1;
    this.tabIndices.fwdRisk = this.permissions.canViewFwdRisk ? tabIndex++ : -1;
    this.tabIndices.clientOrders = this.permissions.canViewClientOrders ? tabIndex++ : -1;
    this.tabIndices.hedgeOrders = this.permissions.canViewHedgeOrders ? tabIndex++ : -1;
    this.tabIndices.audit = this.permissions.canViewBenchmarkAudit ? tabIndex++ : -1;

    if (
      this.permissions.canViewSpotRisk ||
      this.permissions.canViewFwdRisk ||
      this.permissions.canViewClientOrders
    ) {
      lookupPromises.push(this._bmarkDataLookup.isPrimed());
    }

    if (this.permissions.canViewSpotRisk || this.permissions.canViewHedgeOrders) {
      lookupPromises.push(this._hedgeDataLookup.isPrimed());
    }
    Promise.all(lookupPromises).then(() => {
      if (
        this.permissions.canViewSpotRisk ||
        this.permissions.canViewFwdRisk ||
        this.permissions.canViewClientOrders
      ) {
        fixingPromises.push(
          Promise.all([
            this._bmarkDataLookup.getAllActiveBmarkFixings(),
            this._bmarkDataLookup.getAllRecentBmarkFixings(),
          ]).then((values: Array<any>) => {
            // ACTIVE
            if (Array.isArray(values[0]))
              values[0].forEach((value) => (value.source = BenchmarkDataPrimaryLookupService));
            this.updateActiveList(values[0], BenchmarkDataPrimaryLookupService);
            // RECENT
            if (Array.isArray(values[1]))
              values[1].forEach((value) => (value.source = BenchmarkDataPrimaryLookupService));
            this.updateRecentList(values[1], BenchmarkDataPrimaryLookupService);
          }),
        );
      }
      if (this.permissions.canViewSpotRisk || this.permissions.canViewHedgeOrders) {
        fixingPromises.push(
          Promise.all([
            this._hedgeDataLookup.getAllActiveBmarkFixings(),
            this._hedgeDataLookup.getAllRecentBmarkFixings(),
          ]).then((values: Array<any>) => {
            // ACTIVE
            if (Array.isArray(values[0]))
              values[0].forEach((value) => (value.source = HedgeOrderDataPrimaryLookupService));
            this.updateActiveList(values[0], HedgeOrderDataPrimaryLookupService);
            // RECENT
            if (Array.isArray(values[1]))
              values[1].forEach((value) => (value.source = HedgeOrderDataPrimaryLookupService));
            this.updateRecentList(values[1], HedgeOrderDataPrimaryLookupService);
          }),
        );
      }
      Promise.all(fixingPromises).then(() => {
        // Display the first Active
        if (
          this.data.activeFixingsList instanceof Array &&
          this.data.activeFixingsList.length > 0
        ) {
          this.display(this.data.activeFixingsList[0]);
        }
        this.refreshDisplayedList();
        // Subscribe
        this.subscribeToWorkerUpdates();
        // Load tabs
        this.loadActiveTab();
        // Stop event queueing
        this._bmarkDataLookup.bmarkManagerReady();
      });
    });
    this._bmarkUtilService.filterAppliedObs$
      .pipe(filter((val) => val != null))
      .subscribe((filterAppliedObj: FilterAppliedStatusModel) => {
        this.data.isFilterApplied = filterAppliedObj.isFilterApplied;
        this.data.filterConfiguration = filterAppliedObj.filterConfiguration;
      });
  }

  protected initializePermissions(): Promise<void> {
    return new Promise<void>((res) => {
      this._sysConfigLookup.isPrimed().then(() => {
        Promise.all([
          this._sysConfigLookup.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_RISK), // 0
          this._sysConfigLookup.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_ORDERS), // 1
          this._sysConfigLookup.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_HEDGE), // 2
          this._sysConfigLookup.hasRole(Roles.ORDER_BENCHMARK_MANUAL_RISK_ROUTE), // 3
          this._sysConfigLookup.hasRole(Roles.ROLE_BM_AUTO_HEDGING_CIRCUIT_BREAKER), // 4
          this._sysConfigLookup.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_AUDIT), // 5
          this._sysConfigLookup.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_FWD), // 6
        ])
          .then((values: Array<boolean>) => {
            this.permissions.canViewSpotRisk = values[0];
            this.permissions.canViewClientOrders = values[1];
            this.permissions.canViewHedgeOrders = values[2];
            this.permissions.bmRiskRouteEnabled = values[3];
            this.permissions.canUseCircuitBreaker = values[4];
            this.permissions.canViewBenchmarkAudit = values[5];
            this.permissions.canViewFwdRisk = values[6];
            res();
          })
          .catch((_e) => {
            res();
          });
      });
    });
  }

  public getPermissions(): any {
    return this.permissions;
  }

  public getTabType(tabIndex: number): FixingTabType {
    let activeTabType: FixingTabType;
    switch (tabIndex) {
      case this.tabIndices.fwdRisk:
        activeTabType = FixingTabType.FWD_RISK;
        break;
      case this.tabIndices.audit:
        activeTabType = FixingTabType.FIXING_AUDIT;
        break;
      case this.tabIndices.clientOrders:
        activeTabType = FixingTabType.CLIENT_ORDERS;
        break;
      case this.tabIndices.hedgeOrders:
        activeTabType = FixingTabType.HEDGE_ORDERS;
        break;
      default:
        activeTabType = FixingTabType.SPOT_RISK;
        break;
    }
    return activeTabType;
  }

  public getActiveTabType(): FixingTabType {
    return this.getTabType(this.data.activeTabIndex);
  }

  private subscribeToWorkerUpdates(): void {
    if (
      this.permissions.canViewSpotRisk ||
      this.permissions.canViewFwdRisk ||
      this.permissions.canViewClientOrders
    )
      this.fixingsUpdateSubscription = this._bmarkDataLookup
        .getBmarkFixingsUpdateSubject()
        .subscribe(($event) => {
          this.onFixingUpdateEvent($event, BenchmarkDataPrimaryLookupService);
        });
    if (this.permissions.canViewSpotRisk || this.permissions.canViewHedgeOrders)
      this.hedgeFixingsUpdateSubscription = this._hedgeDataLookup
        .getBmarkFixingsUpdateSubject()
        .subscribe(($event) => {
          this.onFixingUpdateEvent($event, HedgeOrderDataPrimaryLookupService);
        });
  }

  /**********************************************************************/
  /* SUBSCRIPTION + HANDLERS  */

  private onFixingUpdateEvent(
    $event: any,
    source: Type<BenchmarkDataPrimaryLookupService | HedgeOrderDataPrimaryLookupService>,
  ): void {
    if (
      $event instanceof Object &&
      $event.hasOwnProperty("operation") &&
      $event.hasOwnProperty("payload")
    ) {
      $event.hasOwnProperty("source");

      if ($event.payload == null) {
        logger.out.warn(
          "${this.constructor.name} Received FIXING update event with no payload: Source " +
            $event.source +
            " Operation " +
            $event.operation,
        );
        return;
      }

      $event.payload.source = source;

      logger.out.info(
        `${this.constructor.name} RECEIVED command ${BmarkFixingOperation[$event.operation]} ${
          source?.name
        }`,
      );
      switch ($event.operation) {
        case BmarkFixingOperation.ADD:
          this.add($event.payload, source);
          break;
        case BmarkFixingOperation.REMOVE:
          this.remove($event.payload, source);
          break;
        case BmarkFixingOperation.MOVE_TO_RECENT:
          this.moveTo($event.payload, BmarkFixingType.RECENT, source);
          break;
        case BmarkFixingOperation.MOVE_TO_ACTIVE:
          this.moveTo($event.payload, BmarkFixingType.ACTIVE, source);
          break;
        case BmarkFixingOperation.UPDATE_DATA:
          this.updateData($event.payload, source);
          break;
        case BmarkFixingOperation.UPDATE_ACTIVE_LIST:
          this.updateActiveList($event.payload, source);
          break;
        case BmarkFixingOperation.UPDATE_RECENT_LIST:
          this.updateRecentList($event.payload, source);
          break;
        default:
          logger.out.error(
            `ERROR: ${this.constructor.name} onFixingUpdateEvent(): Invalid operation ${$event.operation}`,
          );
          break;
      }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name} onFixingUpdateEvent(): Invalid event`,
        $event,
      );
    }
  }

  private checkFixingRawData(fixingDataRaw: any, functionTxt: string): boolean {
    if (
      fixingDataRaw instanceof Object &&
      typeof fixingDataRaw.id === "string" &&
      (fixingDataRaw.type === 1 || fixingDataRaw.type === 0)
    ) {
      return true;
    } else {
      logger.out.error(
        `ERROR: BMARK: ${this.constructor.name}.${functionTxt}(): Invalid FixingData (raw) = %O`,
        fixingDataRaw,
      );
      return false;
    }
  }

  private add(
    fixingData: any,
    _source: Type<BenchmarkDataPrimaryLookupService | HedgeOrderDataPrimaryLookupService>,
  ): void {
    if (this.checkFixingRawData(fixingData, "add")) {
      // recall remove triggered selection as early as possible - for modify
      if (fixingData.id === this.data.pendingSelectAfterRemove.fixingId) {
        clearTimeout(this.data.pendingSelectAfterRemove.timeoutId);
        this.data.pendingSelectAfterRemove = {
          fixingId: null,
          timeoutId: null,
        };
      }

      if (fixingData.type === BmarkFixingType.ACTIVE) {
        let index = this.data.activeFixingsIndexes.has(fixingData)
          ? this.data.activeFixingsIndexes.get(fixingData)
          : -1;
        if (index >= 0) {
          if (this.data.activeFixingsList[index] !== fixingData) {
            // allocator should ensure references are identical
            logger.out.error(`${this.constructor.name} Fixing Data Object Error`);
          }
          logger.out.info(`${this.constructor.name} ADD-ACTIVE fixing already present`);
        } else {
          fixingData.updateDisplayColor();
          fixingData = BmarkFixingData.retain(fixingData);
          index = this.data.activeFixingsList.push(fixingData);
          this.data.activeFixingsIndexes.set(fixingData, index);
          this.sortFixingList(BmarkFixingType.ACTIVE);
          logger.out.info(
            `${this.constructor.name} ADD-ACTIVE -> ACTIVE Fixings (${
              this.data.activeFixingsList.length
            }) => [ ${this.data.activeFixingsList.reduce((p, c) => p + c.id + ", ", "")}]`,
          );
        }
      } else if (fixingData.type === BmarkFixingType.RECENT) {
        let index = this.data.recentFixingsIndexes.has(fixingData)
          ? this.data.recentFixingsIndexes.get(fixingData)
          : -1;
        if (index >= 0) {
          if (this.data.recentFixingsList[index] !== fixingData) {
            // allocator should ensure references are identical
            logger.out.error(`${this.constructor.name}.add() Fixing Data Object Error`);
          }
          logger.out.info(`${this.constructor.name} ADD-RECENT fixing already present`);
        } else {
          fixingData = BmarkFixingData.retain(fixingData);
          this.data.recentFixingsList.push(fixingData);
        }
        this.sortFixingList(BmarkFixingType.RECENT);
        logger.out.info(
          `${this.constructor.name} ADD-RECENT -> RECENT Fixings (${
            this.data.recentFixingsList.length
          }) => [ ${this.data.recentFixingsList.reduce((p, c) => p + c.id + ", ", "")} ]`,
        );
      } else {
        logger.out.error(`ERROR: BMARK: ${this.constructor.name}.add(): Invalid FixingType`);
      }

      this.refreshDisplayedList();
      // Update summary alert status
      this.processFixingForAlerts(fixingData);
    }
  }

  private remove(
    fixingData: any,
    source: Type<BenchmarkDataPrimaryLookupService | HedgeOrderDataPrimaryLookupService>,
  ): void {
    let removed = false;

    if (this.checkFixingRawData(fixingData, "remove")) {
      if (fixingData.type === BmarkFixingType.ACTIVE) {
        let index = this.data.activeFixingsIndexes.has(fixingData)
          ? this.data.activeFixingsIndexes.get(fixingData)
          : -1;
        if (index >= 0) {
          let activeFixing = this.data.activeFixingsList[index];
          if (source === BenchmarkDataPrimaryLookupService) {
            if (!activeFixing.hasHedgeOrders()) {
              this.data.activeFixingsList.splice(index, 1);
              this.data.activeFixingsIndexes.delete(activeFixing);
              this.data.lastFixingStates.delete(activeFixing);
              BmarkFixingData.release(fixingData);
              removed = true;
            } else {
              activeFixing.clearClientOrders();
              activeFixing.source = HedgeOrderDataPrimaryLookupService;
            }
          } else if (source === HedgeOrderDataPrimaryLookupService) {
            if (!activeFixing.hasClientOrders()) {
              this.data.activeFixingsList.splice(index, 1);
              this.data.activeFixingsIndexes.delete(activeFixing);
              this.data.lastFixingStates.delete(activeFixing);
              BmarkFixingData.release(fixingData);
              removed = true;
            } else {
              activeFixing.clearHedgeOrders();
              activeFixing.source = BenchmarkDataPrimaryLookupService;
            }
          }
        }
        logger.out.info(
          `${this.constructor.name} REMOVE-ACTIVE: ACTIVE Fixings (${
            this.data.activeFixingsList.length
          }) => [ ${this.data.activeFixingsList.reduce((p, c) => p + c.id + ", ", "")}]`,
        );
      } else if (fixingData.type === BmarkFixingType.RECENT) {
        let index = this.data.recentFixingsIndexes.has(fixingData)
          ? this.data.recentFixingsIndexes.get(fixingData)
          : -1;
        if (index >= 0) {
          let recentFixing = this.data.recentFixingsList[index];
          if (source === BenchmarkDataPrimaryLookupService) {
            if (!recentFixing.hasHedgeOrders()) {
              this.data.recentFixingsList.splice(index, 1);
              this.data.recentFixingsIndexes.delete(recentFixing);
              this.data.lastFixingStates.delete(recentFixing);
              BmarkFixingData.release(fixingData);
              removed = true;
            } else {
              recentFixing.clearClientOrders();
            }
          } else if (source === HedgeOrderDataPrimaryLookupService) {
            if (!recentFixing.hasClientOrders()) {
              this.data.recentFixingsList.splice(index, 1);
              this.data.recentFixingsIndexes.delete(recentFixing);
              this.data.lastFixingStates.delete(recentFixing);
              BmarkFixingData.release(fixingData);
              removed = true;
            } else {
              recentFixing.clearHedgeOrders();
            }
          }
        }
        logger.out.info(
          `${this.constructor.name} REMOVE-RECENT: Recent Fixings (${
            this.data.recentFixingsList.length
          }) => [${this.data.recentFixingsList.map((item) => item.id)}]`,
        );
      } else {
        logger.out.error(`ERROR: BMARK: removeFixing(): Invalid FixingType`);
      }

      // Update display if selected
      if (
        removed &&
        this.data.selectedFixingId === fixingData.id &&
        this.data.selectedFixingType === fixingData.type
      ) {
        this.data.pendingSelectAfterRemove.fixingId = fixingData.id;
        if (this.data.pendingSelectAfterRemove.timeoutId != null) {
          clearTimeout(this.data.pendingSelectAfterRemove.timeoutId);
        }
        this.data.pendingSelectAfterRemove.timeoutId = setTimeout(() => {
          if (
            this.data.displayedFixingsType === BmarkFixingType.ACTIVE &&
            this.hasFixings(BmarkFixingType.ACTIVE)
          ) {
            this.display(this.data.activeFixingsList[0]);
          } else if (
            this.data.displayedFixingsType === BmarkFixingType.RECENT &&
            this.hasFixings(BmarkFixingType.RECENT)
          ) {
            this.display(this.data.recentFixingsList[0]);
          } else {
            this.display(null);
          }
          this.data.pendingSelectAfterRemove = {
            fixingId: null,
            timeoutId: null,
          };
        }, 50);
      }
      if (removed) {
        this.rebuildIndex(fixingData.type);
        this.refreshDisplayedList();
        // Update summary alert status

        // Ensure that fixing is removed from any active sco and hco lists
        this.processFixingForAlerts({ id: fixingData.id, state: CubiqTS.FixingState.ACTIVE });
      }
    }
  }

  private moveTo(
    fixingData: BmarkFixingData,
    target: BmarkFixingType,
    source: Type<BenchmarkDataPrimaryLookupService | HedgeOrderDataPrimaryLookupService>,
  ) {
    if (this.checkFixingRawData(fixingData, "moveTo")) {
      let selected = false;
      let error = false;

      if (target === BmarkFixingType.ACTIVE) {
        let indexRecent = this.data.recentFixingsIndexes.has(fixingData)
          ? this.data.recentFixingsIndexes.get(fixingData)
          : -1;
        let indexActive = this.data.activeFixingsIndexes.has(fixingData)
          ? this.data.activeFixingsIndexes.get(fixingData)
          : -1;

        if (fixingData.type != BmarkFixingType.ACTIVE) {
          logger.out.error(
            `${this.constructor.name} ${this.constructor.name}.moveTo() Invalid type moved to ACTIVE`,
          );
        }

        if (indexRecent !== -1) {
          this.data.recentFixingsList.splice(indexRecent, 1);
          this.data.recentFixingsIndexes.delete(fixingData);
          this.sortFixingList(BmarkFixingType.RECENT);
        } else {
          logger.out.error(
            `${this.constructor.name} ${this.constructor.name}.moveTo() failed to delete recent during move to active`,
          );
        }

        if (indexActive !== -1) {
          if (this.data.activeFixingsList[indexActive] !== fixingData) {
            logger.out.error(
              `${this.constructor.name} ${this.constructor.name}.moveTo() Fixing Data Object ACTIVE`,
            );
          }
        } else {
          let index = this.data.activeFixingsList.push(fixingData);
          this.data.activeFixingsIndexes.set(fixingData, index);
        }

        selected = BmarkFixingData.getIdAsActive(this.data.selectedFixingId) == fixingData.id;
        this.sortFixingList(BmarkFixingType.ACTIVE);
        this.updateData(fixingData, source);

        logger.out.info(
          `${this.constructor.name} MOVE-TO-ACTIVE: ACTIVE Fixings (${this.data.activeFixingsList.length}) => [ ${fixingData} ]`,
        );
      } else if (target === BmarkFixingType.RECENT) {
        let indexRecent = this.data.recentFixingsIndexes.has(fixingData)
          ? this.data.recentFixingsIndexes.get(fixingData)
          : -1;
        let indexActive = this.data.activeFixingsIndexes.has(fixingData)
          ? this.data.activeFixingsIndexes.get(fixingData)
          : -1;

        if (fixingData.type != BmarkFixingType.RECENT) {
          logger.out.error(
            `${this.constructor.name} ${this.constructor.name}.moveTo() Invalid type moved to Recent`,
          );
        }

        if (indexActive !== -1) {
          this.data.activeFixingsList.splice(indexActive, 1);
          this.data.activeFixingsIndexes.delete(fixingData);
          this.sortFixingList(BmarkFixingType.ACTIVE);
        } else if (indexActive === -1) {
          logger.out.info(
            `${this.constructor.name} ${this.constructor.name}.moveTo() already moved`,
          );
        }

        if (indexRecent !== -1) {
          // no-op - already moved : confirm same Alocator driven instance
          if (this.data.recentFixingsList[indexRecent] !== fixingData) {
            logger.out.error(
              `${this.constructor.name} ${this.constructor.name}.moveTo() Fixing Data Object Error`,
            );
          }
        } else {
          let index = this.data.recentFixingsList.push(fixingData);
          this.data.recentFixingsIndexes.set(fixingData, index);
        }

        selected = BmarkFixingData.getIdAsRecent(this.data.selectedFixingId) == fixingData.id;
        this.sortFixingList(BmarkFixingType.RECENT);
        this.updateData(fixingData, source);
        logger.out.info(
          `${this.constructor.name} MOVE-TO-RECENT: Recent Fixings (${this.data.recentFixingsList.length}) => [ ${fixingData} ]`,
        );
      } else {
        logger.out.error(
          `ERROR: UI-BM-Manager: ${this.constructor.name}.moveTo(): Invalid FixingType`,
        );
        error = true;
      }

      this.refreshDisplayedList();

      // Update display if selected
      if (selected) {
        this.display(fixingData);
      }
      // Update summary alert status
      if (!error) {
        this.processFixingForAlerts(fixingData);
      }
    }
  }

  private updateData(
    fixingData: any,
    _source: Type<BenchmarkDataPrimaryLookupService | HedgeOrderDataPrimaryLookupService>,
  ): void {
    if (this.checkFixingRawData(fixingData, "updateData")) {
      logger.out.info(
        `${this.constructor.name} -> [${fixingData.id}] Processing UPDATE-DATA:  =>`,
        fixingData,
      );
      let error = false;
      let performSort = false;
      try {
        if (fixingData.type === BmarkFixingType.ACTIVE) {
          let indexActive = this.data.activeFixingsIndexes.has(fixingData)
            ? this.data.activeFixingsIndexes.get(fixingData)
            : -1;
          if (indexActive >= 0) {
            if (this.data.lastFixingStates.has(fixingData)) {
              performSort = this.data.lastFixingStates.get(fixingData) !== fixingData.state;
            } else {
              performSort = this.data.activeFixingsList[indexActive].state !== fixingData.state;
            }
            this.data.lastFixingStates.set(fixingData, fixingData.state);
            fixingData.updateDisplayColor();
            this.data.activeFixingsList[indexActive] = fixingData;
            logger.out.info(
              `${this.constructor.name} UPDATE-DATA: Active Fixing [${fixingData.id}] =>`,
              fixingData,
            );
          } else {
            logger.out.error(
              `ERROR: ${this.constructor.name} UPDATE-FIXING-DATA Active Fixing [${fixingData.id}] =>`,
              fixingData,
            );
            error = true;
          }
        } else if (fixingData.type === BmarkFixingType.RECENT) {
          let indexRecent = this.data.recentFixingsIndexes.has(fixingData)
            ? this.data.recentFixingsIndexes.get(fixingData)
            : -1;
          if (indexRecent >= 0) {
            if (
              fixingData.state === CubiqTS.FixingState.HARD_CUTOFF ||
              fixingData.state === CubiqTS.FixingState.SOFT_CUTOFF
            ) {
              logger.out.warn(
                `ERROR: BMARK: ${this.constructor.name}.updateData(): Recent Fixing [${
                  fixingData.id
                }] has invalid state ${CubiqTS.FixingState[fixingData.state]}`,
              );
            }

            this.data.recentFixingsList[indexRecent] = fixingData;
            logger.out.info(
              `${this.constructor.name} UPDATE-DATA: Recent Fixing [${fixingData.id}] =>`,
              fixingData,
            );
          } else {
            logger.out.error(
              `ERROR: ${this.constructor.name} UPDATE-FIXING-DATA Recent Fixing [${fixingData.id}] =>`,
              fixingData,
            );
            error = true;
          }
        } else {
          logger.out.error(`ERROR: BMARK: updateData(): Invalid FixingType`);
          error = true;
        }
      } catch (ex) {
        logger.out.error(`ERROR: BMARK: ${this.constructor.name}.updateData():`, ex);
        error = true;
      }
      // re-sort if  needed
      if (performSort) {
        if (fixingData.type === BmarkFixingType.ACTIVE) this.sortFixingList(BmarkFixingType.ACTIVE);
        if (fixingData.type === BmarkFixingType.RECENT) this.sortFixingList(BmarkFixingType.RECENT);
      }
      this.refreshDisplayedList();

      if (!error) {
        // Update display if selected or las requested
        this.updateDisplayedFixing(fixingData);
        // Update summary alert status
        this.processFixingForAlerts(fixingData);
      }
    }
  }

  private updateRecentList(
    recentFixings: Array<BmarkFixingData>,
    source: Type<BenchmarkDataPrimaryLookupService | HedgeOrderDataPrimaryLookupService>,
  ): void {
    this.data.recentFixingsList = this.data.recentFixingsList.filter(
      (fixing) => fixing.source !== source,
    );
    this.data.recentFixingsIndexes = new WeakMap(); // old indexes are invalidated by filter

    if (recentFixings instanceof Array && recentFixings.length > 0) {
      for (let recentFixing of recentFixings) {
        let index;
        if (BmarkFixingType.RECENT !== recentFixing.type) {
          logger.out.error(
            `${this.constructor.name} UPDATE-RECENT-LIST: Recent List dropping active type fixing data id:${recentFixing.id}`,
          );
          break;
        }
        recentFixing.source = source;

        index = this.data.recentFixingsList.push(recentFixing);
        this.data.recentFixingsIndexes.set(recentFixing, index);
        recentFixing.updateDisplayColor();
      }
      // sorted needed as two updates occur when both roles available
      this.sortFixingList(BmarkFixingType.RECENT);
      logger.out.info(
        `${this.constructor.name} UPDATE-RECENT-LIST: Recent List (${
          this.data.recentFixingsList.length
        }) => [${this.data.recentFixingsList.map((item) => item.id)}]`,
      );
      this.processFixingListForAlerts(this.data.recentFixingsList);
      this.refreshDisplayedList();
    } else {
      logger.out.info(
        `${this.constructor.name} UPDATE-RECENT-LIST: Rebuilt Index (${
          this.data.recentFixingsList.length
        }) => [${this.data.recentFixingsList.map((item) => item.id)}]`,
      );
      this.rebuildIndex(BmarkFixingType.RECENT);
    }
  }

  private updateActiveList(
    activeFixings: Array<any>,
    source: Type<BenchmarkDataPrimaryLookupService | HedgeOrderDataPrimaryLookupService>,
  ): void {
    this.data.activeFixingsList = this.data.activeFixingsList.filter(
      (fixing) => fixing.source !== source,
    );
    this.data.activeFixingsIndexes = new WeakMap(); // old indexes are invalidated by filter

    if (activeFixings instanceof Array && activeFixings.length > 0) {
      for (let activeFixing of activeFixings) {
        let index;

        if (BmarkFixingType.ACTIVE !== activeFixing.type) {
          logger.out.error(
            `${this.constructor.name} UPDATE-ACTIVE-LIST: Active List dropping active type fixing data id:${activeFixing.id}`,
          );
          break;
        }
        activeFixing.source = source;
        index = this.data.activeFixingsList.push(activeFixing);
        this.data.activeFixingsIndexes.set(activeFixing, index);
        activeFixing.updateDisplayColor();
      }
      // sorted needed as two updates occur when both roles available
      this.sortFixingList(BmarkFixingType.ACTIVE);
      logger.out.info(
        `${this.constructor.name} UPDATE-ACTIVE-LIST: Active List (${
          this.data.activeFixingsList.length
        }) => [${this.data.activeFixingsList.map((item) => item.id)}]`,
      );
      this.processFixingListForAlerts(this.data.activeFixingsList);
      this.refreshDisplayedList();
    } else {
      logger.out.info(
        `${this.constructor.name} UPDATE-ACTIVE-LIST: Rebuilt Index (${
          this.data.activeFixingsList.length
        }) => [${this.data.activeFixingsList.map((item) => item.id)}]`,
      );
      this.rebuildIndex(BmarkFixingType.ACTIVE);
    }
  }

  private updateDisplayedFixing(fixingData: any): boolean {
    logger.out.warn(
      `${this.constructor.name}.updateDisplayedFixing() Processing Display Update ` +
        `=> Expected [${this.data?.requestedFixingId}] | Got [${fixingData?.id}] = ` +
        `RESULT: ${
          this.data?.requestedFixingId === fixingData?.id &&
          this.data?.requestedFixingType === fixingData?.type
        }`,
    );

    if (this.checkFixingRawData(fixingData, "updateData")) {
      let dataFixingId: string = fixingData.id;
      let dataFixingType: BmarkFixingType = fixingData.type;
      if (
        this.data.requestedFixingId === dataFixingId &&
        this.data.requestedFixingType === dataFixingType
      ) {
        this.data.selectedFixingId = this.data.requestedFixingId;
        this.data.selectedFixingType = this.data.requestedFixingType;
        //
        this.data.requestedFixingId = null;
        this.data.requestedFixingType = null;
        //
        this._bmarkDataLookup.setDisplayedRecentFixing(dataFixingId);
        this._hedgeDataLookup.setDisplayedRecentFixing(dataFixingId);

        fixingData.updateDisplayColor();

        this.displayFixingEventSource.next(fixingData);

        return true;
      } else if (
        this.data.selectedFixingId === fixingData.id &&
        this.data.selectedFixingType === fixingData.type
      ) {
        fixingData.updateDisplayColor();
        this.refreshSelectedFixingData(fixingData);
        return true;
      }
    }
    return false;
  }

  private refreshDisplayedList(): void {
    this.activeFixingListEventSource.next([...this.data.activeFixingsList]);
    this.recentFixingListEventSource.next([...this.data.recentFixingsList]);
  }

  public refreshSelectedFixingData(fixingData: BmarkFixingData): void {
    this.data.selectedFixingId = fixingData instanceof BmarkFixingData ? fixingData.id : null;
    this.data.selectedFixingType = fixingData instanceof BmarkFixingData ? fixingData.type : null;
    this._bmarkDataLookup.setDisplayedRecentFixing(fixingData.id);
    this._hedgeDataLookup.setDisplayedRecentFixing(fixingData.id);
    this.displayFixingEventSource.next(fixingData);
  }
  /**********************************************************************/
  /* METHODS - SCO/HCO ALERTS */

  private processFixingListForAlerts(fixingList: Array<BmarkFixingData>): void {
    if (Array.isArray(fixingList)) {
      let updateFg = false;
      for (let fixingData of fixingList) {
        updateFg = updateFg || this.processFixingForAlerts(fixingData, true);
      }

      if (updateFg) {
        this.updateSummaryAlertStatus();
      }
    }
  }

  /**
   * Check the fixing state and ensure the fixing is included in the appropriate alert list. If the state
   * is not one of the 2 SCO states, it should not be present in any alert list.
   * if the alert lists have changed after processing, this will emit a new summary alert status notification
   * @param fixingData
   * @param batch
   * @returns
   */
  private processFixingForAlerts(fixingData: FixingType, batch: boolean = false): boolean {
    if (fixingData) {
      //logger.out.info(`${this.constructor.name} PROCESS ALERT STATUS for [${fixingData.id}] => HCO: ${fixingData.state === HARD_CUTOFF ? 'Yes': 'No'} |  SCO: ${fixingData.state === SOFT_CUTOFF ? 'Yes': 'No'} `)
      let updateFg = false;
      if (fixingData.state === CubiqTS.FixingState.HARD_CUTOFF) {
        // add to HCO list if not there
        if (!this.data.hcoIdsList.has(fixingData.id)) {
          this.data.hcoIdsList.add(fixingData.id);
          updateFg = true;
        }
        // remove from SCO List if there
        if (this.data.scoIdsList.has(fixingData.id)) {
          this.data.scoIdsList.delete(fixingData.id);
          updateFg = true;
        }
      } else if (fixingData.state === CubiqTS.FixingState.SOFT_CUTOFF) {
        // remove from HCO list if there
        if (this.data.hcoIdsList.has(fixingData.id)) {
          this.data.hcoIdsList.delete(fixingData.id);
          updateFg = true;
        }
        // add to SCO List if not there
        if (!this.data.scoIdsList.has(fixingData.id)) {
          this.data.scoIdsList.add(fixingData.id);
          updateFg = true;
        }
      } else {
        // remove from HCO list if there
        if (this.data.hcoIdsList.has(fixingData.id)) {
          this.data.hcoIdsList.delete(fixingData.id);
          updateFg = true;
        }
        // remove from SCO List if there
        if (this.data.scoIdsList.has(fixingData.id)) {
          this.data.scoIdsList.delete(fixingData.id);
          updateFg = true;
        }
      }
      // if no fixings change check if order count has changed
      if (!updateFg) {
        let hcoCount = this.doAlertStateOrdersCount(CubiqTS.FixingState.HARD_CUTOFF);
        let scoCount = this.doAlertStateOrdersCount(CubiqTS.FixingState.SOFT_CUTOFF);
        updateFg =
          this.data.alertStatus instanceof BmarkSummaryAlertStatus &&
          (hcoCount !== this.data.alertStatus.hco_orders ||
            scoCount !== this.data.alertStatus.sco_orders);
      }

      if ((updateFg && !batch) || this.data.isFilterApplied || this.reRunUpdateEvent) {
        this.updateSummaryAlertStatus();
        return true;
      } else {
        return updateFg;
      }
    }
    return false;
  }

  private updateSummaryAlertStatus(): void {
    let newAlertStatus = new BmarkSummaryAlertStatus();
    // HCO
    newAlertStatus.hco_alert = this.data.hcoIdsList.size > 0;
    newAlertStatus.hco_ids =
      this.data.hcoIdsList.size > 0 ? new Set<string>(this.data.hcoIdsList) : null;
    newAlertStatus.hco_orders = this.doAlertStateOrdersCount(CubiqTS.FixingState.HARD_CUTOFF);
    // SCO
    newAlertStatus.sco_alert = this.data.scoIdsList.size > 0;
    newAlertStatus.sco_ids =
      this.data.scoIdsList.size > 0 ? new Set<string>(this.data.scoIdsList) : null;
    newAlertStatus.sco_orders = this.doAlertStateOrdersCount(CubiqTS.FixingState.SOFT_CUTOFF);

    // But, is there a change ?
    if (
      this.data.alertStatus == null ||
      newAlertStatus.hco_alert !== this.data.alertStatus.hco_alert ||
      newAlertStatus.sco_alert !== this.data.alertStatus.sco_alert ||
      newAlertStatus.hco_orders !== this.data.alertStatus.hco_orders ||
      newAlertStatus.sco_orders !== this.data.alertStatus.sco_orders ||
      this.reRunUpdateEvent
    ) {
      // houston we have a change
      logger.out.info(`${this.constructor.name} ALERT STATUS UPDATE: `, newAlertStatus);
      this.data.alertStatus = newAlertStatus;
      if (this.data.isFilterApplied) {
        this.processSummaryAlertWithFilterApplied(newAlertStatus);
      } else {
        this.summaryAlertStatusSource.next(newAlertStatus);
      }
    }
    // Decide whether to open BmarkSummary
    if (!this.data.interfaceOnline && (newAlertStatus.hco_alert || newAlertStatus.sco_alert)) {
      logger.out.info(
        `${this.constructor.name} OPEN SUMMARY for ALERT STATUS UPDATE: `,
        newAlertStatus,
      );
      this.openBmarkSummary();
    }
  }

  private openBmarkSummary(): void {
    this._layoutService.showBenchmarkSummary(true);
  }

  private doAlertStateOrdersCount(type: CubiqTS.FixingState): number {
    let count = 0;
    if (type === CubiqTS.FixingState.HARD_CUTOFF && this.data.hcoIdsList.size > 0) {
      for (let hcoId of this.data.hcoIdsList) {
        let fixingData = this.getFixingData(hcoId);
        count += fixingData != null ? fixingData.clientOrdersCount : 0;
      }
    } else if (type === CubiqTS.FixingState.SOFT_CUTOFF && this.data.scoIdsList.size > 0) {
      for (let scoId of this.data.scoIdsList) {
        let fixingData = this.getFixingData(scoId);
        count += fixingData != null ? fixingData.clientOrdersCount : 0;
      }
    }
    return count;
  }

  /**********************************************************************/
  /* METHODS - PUBLIC */

  public setInterfaceState(state: boolean): void {
    this.data.interfaceOnline = state;
  }

  public display(fixingData: BmarkFixingData): void {
    if (fixingData == null) {
      this.data.selectedFixingId = null;
      this.data.selectedFixingType = null;
      this._bmarkDataLookup.clearDisplayedRecentFixing();
      this._hedgeDataLookup.clearDisplayedRecentFixing();
      this.displayFixingEventSource.next(null);
    } else {
      this.data.requestedFixingId = fixingData.id;
      this.data.requestedFixingType = fixingData.type;

      logger.out.info(
        `Fixing Retrieval: UI-BM-Manager -> [${this.data.requestedFixingId}] Display REQUESTED as EVENT`,
      );
      clearTimeout(this.data.pendingSelectAfterRemove.timeoutId);
      this.data.pendingSelectAfterRemove = {
        fixingId: null,
        timeoutId: null,
      };
      this.retrievingOrdersEventSource.next();

      this._bmarkDataLookup.getBmarkFixingDataAsEvent(fixingData.id, fixingData.type);
      this._hedgeDataLookup.getBmarkFixingDataAsEvent(fixingData.id, fixingData.type);
    }
  }

  public reRunUpdateSummaryAlertStatus() {
    if (this.data.hcoIdsList.size > 0 || this.data.scoIdsList.size > 0) {
      // only time to update status
      this.reRunUpdateEvent = true;
      this.updateSummaryAlertStatus();
      this.reRunUpdateEvent = false;
    } else {
      // continue to do the walk and talk the talk
    }
  }

  /**********************************************************************/
  /* PRIVATE METHODS */

  private findDisplayedFixing(fixingId: string): BmarkFixingData | null {
    if (typeof fixingId === "string") {
      let result = null;
      if (
        this.data.displayedFixingsType === BmarkFixingType.ACTIVE &&
        this.hasFixings(BmarkFixingType.ACTIVE)
      ) {
        result = this.data.activeFixingsList.find((item) => item.id === fixingId);
      }

      if (
        this.data.displayedFixingsType === BmarkFixingType.RECENT &&
        this.hasFixings(BmarkFixingType.RECENT)
      ) {
        result = this.data.recentFixingsList.find((item) => item.id === fixingId);
      }
      return result;
    }
    return null;
  }

  private hasFixings(type: BmarkFixingType): boolean {
    if (type in BmarkFixingType) {
      return type === BmarkFixingType.ACTIVE
        ? this.data.activeFixingsList instanceof Array && this.data.activeFixingsList.length > 0
        : this.data.recentFixingsList instanceof Array && this.data.recentFixingsList.length > 0;
    }
    return false;
  }

  private getFixingData(fixingId: string): BmarkFixingData | null {
    if (typeof fixingId === "string") {
      let activeFixingData = this.hasFixings(BmarkFixingType.ACTIVE)
        ? this.data.activeFixingsList.find((item) => item.id === fixingId)
        : null;
      let recentFixingData = this.hasFixings(BmarkFixingType.RECENT)
        ? this.data.recentFixingsList.find((item) => item.id === fixingId)
        : null;
      if (activeFixingData != null && recentFixingData != null) {
        logger.out.warn(
          `WARN: ${this.constructor.name} Fixing [${fixingId}] found in both ACTIVE and RECENT LISTs`,
        );
      }
      return activeFixingData instanceof BmarkFixingData
        ? activeFixingData
        : recentFixingData instanceof BmarkFixingData
          ? recentFixingData
          : null;
    }
    return null;
  }

  private isHCOSortItem(state: CubiqTS.FixingState) {
    return (
      state === CubiqTS.FixingState.HARD_CUTOFF ||
      state === CubiqTS.FixingState.FIXING_HIT ||
      state === CubiqTS.FixingState.TRIGGERED ||
      state === CubiqTS.FixingState.COMPLETED
    );
  }

  private sortFixingList(type: BmarkFixingType): void {
    let fixingList =
      BmarkFixingType.ACTIVE === type
        ? this.data.activeFixingsList
        : BmarkFixingType.RECENT === type
          ? this.data.recentFixingsList
          : [];

    if (fixingList instanceof Array && fixingList.length > 0) {
      fixingList.forEach((a: BmarkFixingData) => {
        this.data.lastFixingStates.set(a, a.state);
      });

      fixingList.sort((a: BmarkFixingData, b: BmarkFixingData) => {
        try {
          let aHard = this.isHCOSortItem(a.state);
          let bHard = this.isHCOSortItem(b.state);

          let aSoft = a.state === CubiqTS.FixingState.SOFT_CUTOFF;
          let bSoft = b.state === CubiqTS.FixingState.SOFT_CUTOFF;
          let aDateTime = a.fixingTimestamp;
          let bDateTime = b.fixingTimestamp;
          if (type === BmarkFixingType.ACTIVE) {
            if ((aHard || bHard) && !(aHard && bHard)) {
              // xor hard
              return aHard ? -1 : 1;
            } else if ((aSoft || bSoft) && !(aSoft && bSoft)) {
              // xor soft
              return aSoft ? -1 : 1;
            } else {
              if (aDateTime === bDateTime) {
                return a.code.localeCompare(b.code);
              }
              return bDateTime > aDateTime ? -1 : 1;
            }
          } else if (type === BmarkFixingType.RECENT) {
            if (aDateTime === bDateTime) {
              return a.code.localeCompare(b.code);
            }
            return aDateTime > bDateTime ? -1 : 1;
          } else {
            logger.out.error(
              `ERROR: UI-BM-Manager: sortFixingList(): Invalid type input parameter!`,
            );
          }
        } catch (ex) {
          logger.out.error(`ERROR: UI-BMs-Manager: Bmark-Fixing-List sorting exception: ${ex}`);
          return 0;
        }
      });
    }

    this.rebuildIndex(type);
  }

  private rebuildIndex(type: BmarkFixingType): void {
    if (BmarkFixingType.ACTIVE === type) {
      this.data.activeFixingsIndexes = new WeakMap();
      this.data.activeFixingsList.forEach((fixingData, index) => {
        this.data.activeFixingsIndexes.set(fixingData, index);
      });
    }

    if (BmarkFixingType.RECENT === type) {
      this.data.recentFixingsIndexes = new WeakMap();
      this.data.recentFixingsList.forEach((fixingData, index) => {
        this.data.recentFixingsIndexes.set(fixingData, index);
      });
    }
  }

  private async processSummaryAlertWithFilterApplied(alertStatus: BmarkSummaryAlertStatus) {
    // only called when filter is applied
    let hco_count = 0;
    let sco_count = 0;
    if (this.data.hcoIdsList.size > 0) {
      for (let hcoId of this.data.hcoIdsList) {
        let fixingData = this.getFixingData(hcoId);
        hco_count += fixingData != null ? await this.getFilterOrderCount(fixingData) : 0;
      }
    }
    if (this.data.scoIdsList.size > 0) {
      for (let scoId of this.data.scoIdsList) {
        let fixingData = this.getFixingData(scoId);
        sco_count += fixingData != null ? await this.getFilterOrderCount(fixingData) : 0;
      }
    }
    alertStatus.hco_orders = hco_count;
    alertStatus.sco_orders = sco_count;
    alertStatus.hco_alert = hco_count > 0;
    alertStatus.sco_alert = sco_count > 0;
    this.summaryAlertStatusSource.next(alertStatus);
  }

  private async getFilterOrderCount(fixing: BmarkFixingData): Promise<number> {
    let filteredOutOrderListSize: number = fixing.clientOrdersCount;

    let orderDataArray: Array<BmarkOrderData> =
      await this._bmarkDataLookup.getBmarkOrderDataForFixingId(fixing.id, fixing.type);
    let filterList: BfxBlotterFilter[] = await this._blotterFilterService.getItems(
      FilterType.BlotterFilter,
      new BfxBlotterFilterId(CubiqTS.BlotterType.ORDER, CubiqTS.AssetClass.FIX),
      ExtendedOrderTypeIdentifier.BENCHMARK,
    );
    let selectedFilter: Array<BfxBlotterFilter> = filterList.filter(
      (filterItem: BfxBlotterFilter) =>
        this.data.filterConfiguration.indexOf(filterItem.filterId) >= 0,
    );

    let isOrderListNotEmpty = orderDataArray != null && orderDataArray.length > 0;
    if (isOrderListNotEmpty) {
      filteredOutOrderListSize = orderDataArray.filter((order: any) =>
        this._bmarkFilteringService.doFiltersPass(order, selectedFilter),
      ).length;
    }
    return filteredOutOrderListSize;
  }

  /**********************************************************************/
  /* TAB MANAGEMENT */

  public setActiveTab(tabIndex: number): void {
    logger.out.info(`${this.constructor.name} Display tab: ${tabIndex}`);
    if (this.data.activeTabIndex !== tabIndex) {
      this.data.activeTabIndex = tabIndex;
      this.saveActiveTab(tabIndex);
      const tabType: FixingTabType = this.getTabType(tabIndex);
      this.activeTabSource.next({ tabIndex: tabIndex, activeTabType: tabType });
    }
  }

  private saveActiveTab(index: number): void {
    let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];
    profileSettings.push(
      this._userProfileService.createProfileSettingObject(PROFILE_KEY, JSON.stringify(index)),
    );
    this._profileService.updateProfile(profileSettings, false).then((_value: any) => {
      this._activeUserService.getActiveUserData().then((userData: ActiveUser) => {
        if (userData.profileSettings != null) {
          userData.profileSettings.set(PROFILE_KEY, JSON.stringify(index));
          this._activeUserService.setActiveUserData(userData);
        }
      });
    });
  }

  private loadActiveTab(): void {
    this._activeUserService.getActiveUserData().then((userData: ActiveUser) => {
      if (userData.profileSettings != null) {
        try {
          let activeTabIndex = parseInt(userData.profileSettings.get(PROFILE_KEY));
          if (typeof activeTabIndex === "number") {
            this.data.activeTabIndex = activeTabIndex;
            const tabType: FixingTabType = this.getTabType(activeTabIndex);
            this.activeTabSource.next({ tabIndex: activeTabIndex, activeTabType: tabType });
          } else {
            logger.out.warn(
              `ERROR: ${this.constructor.name} loadActiveTab(): Invalid active tab value ${activeTabIndex}, not a number.`,
            );
          }
        } catch (ex) {
          logger.out.warn(
            `ERROR: ${this.constructor.name} loadActiveTab(): Invalid active tab value in profile.`,
          );
        }
      }
    });
  }
  /**********************************************************************/
  /* DEBUG - HELPER  */

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputExtendedLogging(value, dump);
  }
}
